import { useTranslation } from 'react-i18next';

import { useCashOutSheets } from '@/components/CashOutSheet';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { withCashOutAnalytics } from '@/hooks/cash-out-analytics';
import { useCashOutOffer } from '@/hooks/cash-out-offers-context';
import { useEntryCashOut } from '@/hooks/use-entry-cash-out';
import type { CashOutResult, EntryCashOut } from '@/hooks/use-entry-cash-out';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { logger } from '@/utils/logging';
import { sleep } from '@/utils/promises';
import { useQueryClient } from '@tanstack/react-query';

import { useSbkCurrencyIcon, useSbkCurrencyIconSm } from '../components/bet-cards/sbk-entry-card-currency';
import { Bet, CashoutBetError, GetBetCashoutAmountResponse } from './types';
import { getBetDetail } from './use-bet-detail';
import { betKeys } from './use-bets';
import { useCashoutBet } from './use-cashout';
import { useSbkCashedOutEntries } from './use-sbk-cashed-out-entries';

const CASH_OUT_LOG_TAG = 'SbkEntryCashOut';

const SETTLEMENT_POLL_ATTEMPTS = 3;
const SETTLEMENT_POLL_INTERVAL_MS = 1000;

/**
 * The sell response body is unreliable, so read the settled bet back from the bet endpoint. The first
 * attempt fires immediately: a filled (200) sell usually settles it in a single fetch.
 */
const pollSettledBet = async (betId: string): Promise<Bet | null> => {
    for (let attempt = 0; attempt < SETTLEMENT_POLL_ATTEMPTS; attempt++) {
        if (attempt > 0) {
            await sleep(SETTLEMENT_POLL_INTERVAL_MS);
        }
        try {
            const { data } = await getBetDetail(betId);
            if (data.result !== 'PENDING') {
                return data;
            }
        } catch (error) {
            logger.warn(CASH_OUT_LOG_TAG, `Settlement poll ${attempt + 1} failed for bet ${betId}`, error);
        }
    }
    return null;
};

/** SBK's adapter over the shared cash-out machine (`useEntryCashOut`): Chameleon REST, session pin, copy. */
export const useSbkEntryCashOut = (bet: Bet): EntryCashOut => {
    const { t } = useTranslation(['bets', 'common']);
    const { formatCurrency } = useFormatCurrency();
    const isPredictions = useIsPredictionsProduct();
    const payoutIcon = useSbkCurrencyIcon();
    const currencyIcon = useSbkCurrencyIconSm();
    // currencyIcon is already undefined under Predictions, where formatCurrency includes the symbol.
    const { showPriceChange, showCashOutError } = useCashOutSheets({
        variant: isPredictions ? 'sellPosition' : 'cashOut',
        currencyNode: currencyIcon,
    });
    const queryClient = useQueryClient();
    const { mutateAsync: cashoutBet } = useCashoutBet();

    const offer = useCashOutOffer(bet.id);
    const pin = useSbkCashedOutEntries(s => s.entries.get(bet.id)) ?? null;
    const markCashedOut = useSbkCashedOutEntries(s => s.markCashedOut);

    // The pin only stands in for server state that hasn't caught up, so drop it once the bet reports settled:
    // the detail screen renders settled entries too, and would re-dock the confirmed footer on every visit.
    const cachedCashedOut = bet.result === 'PENDING' ? pin : null;

    // Can't cashout/sell a predictions position until PM confirms the position, so keep the button hidden.
    const canCashOutPredictionsPosition = bet.can_be_cashed_out && (bet.positions?.length ?? 0) > 0;
    const canCashOut = isPredictions ? canCashOutPredictionsPosition : bet.can_be_cashed_out;

    const confirmCashOut = async (amount: number): Promise<CashOutResult<Bet>> => {
        try {
            await cashoutBet({ betId: bet.id, amount });
        } catch (error) {
            if (error instanceof CashoutBetError && error.code === 'cashout_offer_change' && error.cashable_amount) {
                return { type: 'offerChange', amount: error.cashable_amount };
            }
            throw error;
        }

        if (isPredictions) {
            // The quoted cash out amount isn't the real proceeds (fees/slippage), so only confirm with the bet read back
            // from the server. An accepted (202 response status) sell can still be rejected late, so still-pending after
            // the polls is treated as failed.
            const settled = await pollSettledBet(bet.id);
            if (!settled) {
                queryClient.invalidateQueries({ queryKey: betKeys.detail(bet.id) });
                throw new Error(`Sell still pending after ${SETTLEMENT_POLL_ATTEMPTS} settlement polls`);
            }
            queryClient.setQueryData(betKeys.detail(bet.id), { data: settled });
            return { type: 'success', entry: settled };
        }

        // Sweeps settles at exactly the posted amount, so the quote is the real payout; synthesize the
        // confirmed entry and let the invalidated detail query catch up in the background.
        queryClient.invalidateQueries({ queryKey: betKeys.detail(bet.id) });
        const settled: Bet = {
            ...bet,
            has_been_cashed_out: true,
            result: 'CASHEDOUT',
            amount_won: amount,
            settled_at: Date.now(),
        };
        return { type: 'success', entry: settled };
    };

    const onError = (error: unknown, retry: () => void) => {
        const code = error instanceof CashoutBetError ? error.code : undefined;
        logger.warn(CASH_OUT_LOG_TAG, `Inline cash-out failed for bet ${bet.id}`, error);
        BetrAnalytics.trackProductEvent(AnalyticsEvent.CASH_OUT_ERROR);
        showCashOutError({ code, onRetry: retry });
    };

    const onOfferChange = ({ previous, next, complete }: { previous: number; next: number; complete: () => void }) => {
        // Write the re-price into every offers query (list and detail share the prefix) so the idle CTA reads it
        // before the refetch lands.
        queryClient.setQueriesData<GetBetCashoutAmountResponse>(
            { queryKey: betKeys.cashoutAmounts() },
            old => old && { ...old, data: { ...old.data, [bet.id]: next } }
        );
        queryClient.invalidateQueries({ queryKey: betKeys.cashoutAmounts() });
        BetrAnalytics.trackProductEvent(AnalyticsEvent.CASH_OUT_PRICE_CHANGED, { previous, next });
        showPriceChange({ previous, next, onComplete: complete });
    };

    const cashOut = useEntryCashOut<Bet>({
        canCashOut,
        offer,
        cashedOutAmount: settled => settled?.amount_won ?? bet.amount_won,
        confirmCashOut,
        cachedCashedOut,
        markCashedOut: settled => {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.CASH_OUT_SUCCESS, { amount: settled.amount_won });
            markCashedOut(bet.id, settled);
        },
        onError,
        onOfferChange,
        formatCurrency,
        labels: {
            controls: {
                offer: isPredictions ? t('bets:sell_position') : t('bets:cash_out'),
                confirm: isPredictions ? t('bets:confirm_sell') : t('bets:confirm_cash_out'),
                cancel: t('common:cancel'),
                confirmed: isPredictions ? t('bets:position_sold') : t('bets:cash_out_confirmed'),
            },
            payoutColumn: {
                payout: t('bets:payout'),
                cash_out: isPredictions ? t('bets:sell_position') : t('bets:cash_out'),
            },
        },
        payoutIcon,
        currencyIcon,
    });

    return withCashOutAnalytics(cashOut);
};
